import React, {Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {ConfigurableInfoBlockInputTypes} from "../index";
import {OutlinedInput} from "../../../base/input/outlined_input";
import {OutlinedDropdown} from "../../../base/dropdown/outlined_dropdown";
import {deepEqual} from "../../../../../core/services/utils/utils";
import ToggleButtons from "../../../base/button/toggle_buttons";
import useIndustry from "../../../../hooks/use-industry";
import CKEditorTextfield from "../../../text-fields/ck-editor-textfield";
import useSelectedCategory from "../../../../hooks/use-selected-category";
import AutocompleteBase from "../../../text-fields/autocomplete/autocomplete-base";
import FormulaInput from "./formula-input";
import TagAutocomplete from "../../../text-fields/autocomplete/tag-autocomplete";
import FormsAutocomplete from "../../../text-fields/autocomplete/forms-autocomplete";
import {Alert, Box, Skeleton, Typography} from "@mui/material";
import {ChevronRight} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import useAccessLevels from "../../../../hooks/use-access-levels";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";

const ConfigurableInfoBlockInput = ({
                                        inputType,
                                        loading,
                                        onChanged,
                                        value: valueProps = '',
                                        data = [],
                                        feedback,
                                        removeOnCommit = false,
                                        commitOnEnterPress = false,
                                        commitOnIconClick = false,
                                        ...props
                                    }) => {
    const [value, setValue] = useState(valueProps);
    const erroneous = useMemo(() => !!feedback.show && !feedback.success, [feedback])

    /**
     * With each change in valueProps:
     * - updates the value state.
     */
    useEffect(() => setValue(valueProps), [valueProps])

    /**
     * Commits the changes by calling the onChanged callback.
     *
     * * if removeOnCommit, then removes the value as well.
     * @param value
     */
    const commitChange = (value) => {
        onChanged(value);
        if (removeOnCommit) {
            setValue('');
        }
    }

    /**
     * Calls the onChanged callback only when the new value of the input has changed.
     * @param {Event} e
     */
    const onInputBlurred = (e) => {
        const data = e.target?.value ?? '';
        if (loading || valueProps === data)
            return;
        commitChange(data);
    }

    /**
     * Determines if the changes need to be committed if a key was pressed for the input.
     * @param {KeyboardEvent} e
     * @param {string | null} _data
     */
    const onCKEditorKeyPress = (e, _data = null) => {
        const data = _data ?? e.currentTarget?.querySelector('input')?.value;
        if (loading || valueProps === data)
            return;
        if (commitOnEnterPress && e.key === 'Enter') {
            commitChange(data);
        }
    }

    /**
     * Determines if the changes need to be committed when the enter key was pressed.
     * @param e {KeyboardEvent}
     */
    const onInputKeyPress = (e) => {
        if (loading || valueProps === e.target?.value)
            return;
        if (commitOnEnterPress && e.key === 'Enter') {
            commitChange(e.target?.value ?? "");
        }
    }

    /**
     * Determines if the changes need to be committed if the icon of the input was clicked.
     * @param {MouseEvent} e
     */
    const onInputAdornmentIconClicked = (e) => {
        const data = e.currentTarget?.parentElement?.parentElement?.querySelector('input')?.value;
        if (loading || valueProps === data || !commitOnIconClick)
            return;
        commitChange(data);
    }

    /**
     * Calls the onChanged callback only when the new value of the dropdown has changed.
     * @param {unknown} item
     */
    const onItemSelected = (item) => {
        if (loading || deepEqual(valueProps, item))
            return;
        commitChange(item);
    }

    /**
     * Calls the onChanged callback only when a toggle button has been selected.
     * @param {Event} event
     * @param {unknown} item
     */
    const onToggleButtonClicked = (event, item) => {
        if (loading || deepEqual(valueProps, item))
            return;
        commitChange(item);
    }

    /**
     * Calls the onChanged callback only when the new value of the dropdown has changed.
     * @param {string} data
     */
    const onCKEditorBlurred = (data) => {
        if (loading || deepEqual(valueProps, data))
            return;
        commitChange(data);
    }


    return (
        <>
            {
                inputType === ConfigurableInfoBlockInputTypes.input &&
                <OutlinedInput
                    {...props}
                    value={value}
                    disabled={loading || props?.disabled}
                    fullWidth
                    onChange={setValue}
                    error={erroneous}
                    onBlur={onInputBlurred}
                    onKeyDown={onInputKeyPress}
                    onIconClick={onInputAdornmentIconClicked}
                />
            }
            {
                inputType === ConfigurableInfoBlockInputTypes.select &&
                <OutlinedDropdown
                    {...props}
                    value={value}
                    disabled={loading || props?.disabled}
                    data={data}
                    fullWidth
                    error={erroneous}
                    onChange={onItemSelected}
                />
            }
            {
                inputType === ConfigurableInfoBlockInputTypes.toggle &&
                <ToggleButtons
                    className={'toggle-buttons'}
                    value={value}
                    onChange={onToggleButtonClicked}
                    options={data.map(e => ({
                        ...e,
                        disabled: loading || props?.disabled,
                    }))}
                    {...props}
                />
            }
            {
                inputType === ConfigurableInfoBlockInputTypes.ckEditor &&
                <CKEditorTextfield
                    value={value}
                    disabled={loading || props?.disabled}
                    onChange={setValue}
                    error={erroneous}
                    onBlur={onCKEditorBlurred}
                    onKeyPress={onCKEditorKeyPress}
                />
            }
            {
                inputType === ConfigurableInfoBlockInputTypes.category &&
                <CategoriesInput
                    value={valueProps}
                    onChanged={onChanged}
                    loading={loading}
                    error={erroneous}
                    {...props}
                />
            }
            {
                inputType === ConfigurableInfoBlockInputTypes.tagsAutoComplete &&
                <TagAutocomplete
                    value={null}
                    error={erroneous}
                    onChange={(_, t) => onChanged(t)}
                    clearOnBlur={removeOnCommit}
                    disabled={loading}
                    fullWidth
                    {...props}
                />
            }
            {
                inputType === ConfigurableInfoBlockInputTypes.formsAutoComplete &&
                <FormsAutocomplete
                    value={null}
                    error={erroneous}
                    onChange={(_, t) => onChanged(t)}
                    clearOnBlur={removeOnCommit}
                    disabled={loading}
                    fullWidth
                    {...props}
                />
            }
            {
                inputType === ConfigurableInfoBlockInputTypes.autocomplete &&
                <AutocompleteBase
                    value={valueProps}
                    onChange={(event, value) => onChanged(value)}
                    clearOnBlur={removeOnCommit}
                    disabled={loading}
                    textFieldProps={{
                        error: erroneous,
                        ...(props?.textFieldProps ?? {}),
                    }}
                    {...props}
                />
            }
            {
                inputType === ConfigurableInfoBlockInputTypes.formula &&
                <FormulaInput
                    value={value}
                    priceLevels={props?.priceLevels ?? []}
                    resolvedValues={props?.resolvedValues ?? {}}
                    specialResolvedValues={props?.specialResolvedValues ?? {}}
                    disabled={loading || props?.disabled}
                    error={erroneous}
                    circularError={props?.circularError ?? null}
                    inputProps={props?.inputProps}
                    autoFocus={props?.autoFocus}
                    onChange={setValue}
                    onBlur={(e) => {
                        const data = e.target?.value ?? '';
                        if (!loading && valueProps !== data) {
                            commitChange(data);
                        }
                        props?.onBlur?.(e);
                    }}
                    onKeyDown={(e) => {
                        props?.onKeyDown?.(e);
                        if (e.defaultPrevented) return;
                        if (loading || valueProps === e.target?.value) return;
                        if (commitOnEnterPress && e.key === 'Enter') {
                            commitChange(e.target?.value ?? '');
                        }
                    }}
                />
            }
        </>
    )
}

/**
 * Returns true when the category id and every ancestor exist in the active list.
 * An empty id is treated as resolved so the editor stays on the normal cascade.
 * @param {Array<{id: number|string, parentId?: number|string|null}>} categories
 * @param {number|string|null|undefined} categoryId
 * @returns {boolean}
 */
const isCategoryChainResolved = (categories, categoryId) => {
    if (categoryId == null || categoryId === '') {
        return true;
    }
    const byId = new Map((categories ?? []).map((category) => [String(category.id), category]));
    let current = byId.get(String(categoryId));
    if (!current) {
        return false;
    }
    const seen = new Set();
    while (current?.parentId) {
        if (seen.has(String(current.id))) {
            return false;
        }
        seen.add(String(current.id));
        const parent = byId.get(String(current.parentId));
        if (!parent) {
            return false;
        }
        current = parent;
    }
    return true;
};

/**
 * Builds a one-segment path from the category already stored on the product.
 * @param {{id?: number, title?: string}|null|undefined} fallbackCategory
 * @returns {Array<{id?: number, title?: string}>}
 */
const titleOnlyCategoryPath = (fallbackCategory) => {
    if (!fallbackCategory?.title) {
        return [];
    }
    return [{id: fallbackCategory.id, title: fallbackCategory.title}];
};

/**
 * Classifies a category missing from the active list using the full catalog.
 * @param {Array<{id: number, title?: string, parentId?: number|null, selected?: boolean}>} catalog
 * @param {number|string} categoryId
 * @param {{id?: number, title?: string}|null|undefined} fallbackCategory
 * @returns {{status: 'disabled'|'removed'|'unavailable', path: Array<{id?: number, title?: string, selected?: boolean}>}}
 */
const classifyUnavailableCategory = (catalog, categoryId, fallbackCategory) => {
    const byId = new Map((catalog ?? []).map((category) => [String(category.id), category]));
    const node = byId.get(String(categoryId));
    if (!node) {
        return {status: 'removed', path: titleOnlyCategoryPath(fallbackCategory)};
    }
    const path = [];
    const seen = new Set();
    let current = node;
    let disabled = false;
    while (current && !seen.has(String(current.id))) {
        seen.add(String(current.id));
        path.unshift(current);
        if (current.selected === false) {
            disabled = true;
        }
        if (!current.parentId) {
            break;
        }
        const parent = byId.get(String(current.parentId));
        if (!parent) {
            break;
        }
        current = parent;
    }
    if (disabled) {
        return {status: 'disabled', path};
    }
    return {
        status: 'unavailable',
        path: path.length ? path : titleOnlyCategoryPath(fallbackCategory),
    };
};

/**
 * Keeps the catalog fields needed to rebuild a category path.
 * @param {Array<{id: number, title?: string, parentId?: number|null, selected?: boolean}>} rows
 * @returns {Array<{id: number, title?: string, parentId?: number|null, selected?: boolean}>}
 */
const mapCatalogCategories = (rows) => (rows ?? []).map((category) => ({
    id: category.id,
    title: category.title,
    parentId: category.parentId,
    selected: category.selected,
}));

const categoryFieldSx = {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: {xs: '100%', md: 220},
    flexBasis: {xs: '100%', md: 'calc((100% - 16px) / 2)', xl: 'calc((100% - 32px) / 3)'},
    maxWidth: {xs: '100%', md: 'calc((100% - 16px) / 2)', xl: 'calc((100% - 32px) / 3)'},
};

const unavailableCategoryAlertKeys = {
    disabled: 'disabledAlert',
    removed: 'removedAlert',
    unavailable: 'unavailableAlert',
};

/**
 * Read-only category path joined by chevrons.
 * @param {{segments?: Array<{id?: number, title?: string, selected?: boolean}>, ariaLabel: string}} props
 */
const CategoryArrowPath = ({segments, ariaLabel}) => {
    const visibleSegments = segments?.filter((segment) => segment?.title) ?? [];
    if (!visibleSegments.length) {
        return null;
    }
    return (
        <Box
            aria-label={ariaLabel}
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 0.5,
                rowGap: 0.5,
                width: '100%',
            }}
        >
            {visibleSegments.map((segment, index) => (
                <Fragment key={`${segment.id ?? 'segment'}-${index}`}>
                    {index > 0 && <ChevronRight fontSize="small" sx={{color: 'text.disabled'}}/>}
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{
                            fontWeight: index === visibleSegments.length - 1 ? 600 : 400,
                            color: segment.selected === false ? 'text.disabled' : 'text.primary',
                        }}
                    >
                        {segment.title}
                    </Typography>
                </Fragment>
            ))}
        </Box>
    );
};

/**
 * Cascading category dropdowns for one selection.
 * @param {{
 *   hierarchy: Array<{id: number, title?: string, options?: Array<{id: number, title?: string}>}>,
 *   subCategories: Array<{id: number, title?: string}>,
 *   onSelect: (categoryId: number) => void,
 *   loading?: boolean,
 *   error?: boolean,
 *   dropdownProps?: object,
 * }} props
 */
const CategoryCascadeDropdowns = ({
                                      hierarchy,
                                      subCategories,
                                      onSelect,
                                      loading,
                                      error,
                                      dropdownProps,
                                  }) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.configurableInfoBlock.category'});
    return (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%'}}>
            {hierarchy.map((category, index) => (
                <Box key={`category-${category.id ?? index}`} sx={categoryFieldSx}>
                    <OutlinedDropdown
                        {...dropdownProps}
                        label={index === 0 ? t('category') : t('subCategory')}
                        data={category.options?.map(e => ({label: e.title, value: e.id, id: e.id})) ?? []}
                        onChange={onSelect}
                        value={category.id}
                        SelectProps={{
                            renderValue: () => category.title,
                        }}
                        disabled={loading || dropdownProps?.disabled}
                        fullWidth
                        error={error}
                    />
                </Box>
            ))}
            {subCategories.length > 0 &&
                <Box sx={categoryFieldSx}>
                    <OutlinedDropdown
                        {...dropdownProps}
                        label={hierarchy.length ? t('subCategory') : t('category')}
                        data={subCategories?.map(e => ({label: e.title, value: e.id, id: e.id})) ?? []}
                        onChange={onSelect}
                        value={hierarchy[hierarchy.length - 1]?.id ?? ''}
                        SelectProps={{
                            renderValue: (selectedId) => subCategories.find(c => c.id === selectedId)?.title
                        }}
                        disabled={loading || dropdownProps?.disabled}
                        fullWidth
                        error={error}
                    />
                </Box>
            }
        </Box>
    );
};

const CategoriesInput = ({
                             value: valueProps,
                             onChanged,
                             industryId,
                             loading,
                             error,
                             fallbackCategory,
                             ...props
                         }) => {
    const [value, setValue] = useState(valueProps);
    const [replacementValue, setReplacementValue] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [catalogIndustryId, setCatalogIndustryId] = useState(null);
    const [catalogFailureIndustryId, setCatalogFailureIndustryId] = useState(null);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [activeCategoriesReady, setActiveCategoriesReady] = useState(false);
    const categoryLoadObserved = useRef(false);
    const onChangedRef = useRef(onChanged);
    const pendingSelectionRef = useRef(null);
    const {
        categoriesOfFetchedIndustry: categories,
        fetchIndustry,
        fetchedIndustry,
        loadingFetchedCategories,
        hasNoIndustries,
    } = useIndustry();
    const {packages} = useAccessLevels();
    const canManageCategories = !!packages?.lookup?.manageCategories;
    const isMounted = useIsMounted();
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.configurableInfoBlock.category'});

    const chainResolved = useMemo(
        () => isCategoryChainResolved(categories, valueProps),
        [categories, valueProps],
    );
    const unresolved = activeCategoriesReady && !hasNoIndustries && !chainResolved;

    const {subCategories, selectedCategoriesHierarchy} = useSelectedCategory(
        categories,
        unresolved ? undefined : value,
    );
    const {
        subCategories: replacementSubCategories,
        selectedCategoriesHierarchy: replacementHierarchy,
    } = useSelectedCategory(categories, replacementValue || undefined);

    const awaitingCatalog = unresolved
        && canManageCategories
        && catalogIndustryId !== industryId
        && catalogFailureIndustryId !== industryId;

    const unavailableDetails = useMemo(() => {
        if (!unresolved) {
            return null;
        }
        if (!canManageCategories || catalogFailureIndustryId === industryId) {
            return {status: 'unavailable', path: titleOnlyCategoryPath(fallbackCategory)};
        }
        if (catalogIndustryId !== industryId) {
            return null;
        }
        return classifyUnavailableCategory(catalog, valueProps, fallbackCategory);
    }, [
        unresolved,
        canManageCategories,
        catalogFailureIndustryId,
        industryId,
        catalogIndustryId,
        catalog,
        valueProps,
        fallbackCategory,
    ]);

    /**
     * Selects a category in the active cascade.
     * @type {(function(*): void)}
     */
    const onActiveCategorySelected = useCallback((categoryId) => {
        pendingSelectionRef.current = categoryId;
        setValue(categoryId);
    }, []);

    /**
     * Selects a replacement category without changing the saved assignment until it is a leaf.
     * @type {(function(*): void)}
     */
    const onReplacementCategorySelected = useCallback((categoryId) => {
        pendingSelectionRef.current = categoryId;
        setReplacementValue(categoryId);
    }, []);

    /**
     * With each change in industry:
     * - waits for a completed active-category fetch before deciding the saved category is missing.
     */
    useEffect(() => {
        categoryLoadObserved.current = false;
        setActiveCategoriesReady(false);
    }, [industryId]);

    /**
     * Marks the active category list ready after its load cycle finishes for this industry.
     */
    useEffect(() => {
        if (!industryId || String(fetchedIndustry?.id) !== String(industryId)) {
            return;
        }
        if (loadingFetchedCategories) {
            categoryLoadObserved.current = true;
            return;
        }
        if (categoryLoadObserved.current) {
            setActiveCategoriesReady(true);
        }
    }, [industryId, fetchedIndustry?.id, loadingFetchedCategories]);

    /**
     * Keeps the save callback current without re-running the leaf-commit effects.
     */
    useLayoutEffect(() => {
        onChangedRef.current = onChanged;
    }, [onChanged]);

    /**
     * With each change in valueProps:
     * - keeps the active cascade aligned with the saved category
     * - clears a replacement draft and any unsaved user selection from the previous assignment
     */
    useLayoutEffect(() => {
        pendingSelectionRef.current = null;
        setValue(valueProps);
        setReplacementValue(null);
    }, [valueProps]);

    /**
     * With each change in industryId:
     * - loads the industry whose active categories should be searched
     */
    useLayoutEffect(() => {
        if (industryId) {
            void fetchIndustry({industryId: industryId});
        }
    }, [fetchIndustry, industryId]);

    const activeChildCount = subCategories.length;
    const replacementChildCount = replacementSubCategories.length;

    /**
     * With each change in the visible cascade selection:
     * - saves the category the user just picked once it has no active children.
     * Only ids recorded by the selection handlers are saved; ids synced from the saved
     * category are never re-submitted.
     */
    useEffect(() => {
        const pending = pendingSelectionRef.current;
        if (pending == null || pending === '') {
            return;
        }
        const selection = unresolved ? replacementValue : value;
        const selectionChildCount = unresolved ? replacementChildCount : activeChildCount;
        if (String(selection) !== String(pending) || selectionChildCount > 0) {
            return;
        }
        pendingSelectionRef.current = null;
        if (String(pending) === String(valueProps)) {
            return;
        }
        onChangedRef.current(pending);
    }, [
        unresolved,
        value,
        replacementValue,
        activeChildCount,
        replacementChildCount,
        valueProps,
    ]);

    /**
     * When the saved category is missing from the active list and category management is allowed:
     * - loads the full catalog so the path can be classified as disabled or removed.
     */
    useEffect(() => {
        if (!unresolved || !canManageCategories || !industryId) {
            return;
        }
        if (catalogIndustryId === industryId || catalogFailureIndustryId === industryId) {
            return;
        }
        let cancelled = false;
        setCatalogLoading(true);
        const loadCatalog = async () => {
            const response = await Api.getAllPartCategoriesNew(industryId);
            if (cancelled || !isMounted()) {
                return;
            }
            setCatalogLoading(false);
            if (response?.resultFlag) {
                setCatalog(mapCatalogCategories(Array.isArray(response.data) ? response.data : []));
                setCatalogIndustryId(industryId);
                setCatalogFailureIndustryId(null);
                return;
            }
            setCatalogFailureIndustryId(industryId);
        };
        void loadCatalog();
        return () => {
            cancelled = true;
        };
    }, [
        unresolved,
        canManageCategories,
        industryId,
        catalogIndustryId,
        catalogFailureIndustryId,
        isMounted,
    ]);

    return (
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1}}>
            {unresolved && (awaitingCatalog || catalogLoading) &&
                <Skeleton variant="rounded" height={72} sx={{width: '100%'}}/>
            }
            {unresolved && unavailableDetails &&
                <Alert
                    severity="warning"
                    sx={{border: theme => `1px solid ${theme.palette.warning.main}`}}
                >
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                        <span>
                            {t(unavailableCategoryAlertKeys[unavailableDetails.status] ?? 'unavailableAlert')}
                        </span>
                        <CategoryArrowPath
                            segments={unavailableDetails.path}
                            ariaLabel={t('pathAriaLabel')}
                        />
                    </Box>
                </Alert>
            }
            {hasNoIndustries &&
                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 1}}>
                    {t('noIndustries')}
                </Typography>
            }
            {!hasNoIndustries && unresolved &&
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
                    <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.2}}>
                        {t('assignAnother')}
                    </Typography>
                    <CategoryCascadeDropdowns
                        hierarchy={replacementHierarchy}
                        subCategories={replacementSubCategories}
                        onSelect={onReplacementCategorySelected}
                        loading={loading}
                        error={error}
                        dropdownProps={props}
                    />
                </Box>
            }
            {!hasNoIndustries && !unresolved &&
                <CategoryCascadeDropdowns
                    hierarchy={selectedCategoriesHierarchy}
                    subCategories={subCategories}
                    onSelect={onActiveCategorySelected}
                    loading={loading}
                    error={error}
                    dropdownProps={props}
                />
            }
        </Box>
    );
};

export default ConfigurableInfoBlockInput;
